import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  listProvenanceDecisions,
  getProvenanceDecision,
  getProvenanceStats,
  exportComplianceArtifact,
  getCascadeForDecision,
} from '../services/api';
import SCGRuleLink from '../components/SCGRuleLink';
import ViewInPdfButton from '../components/ViewInPdfButton';

const KIND_COLORS = {
  classification: { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa' },
  tearline: { bg: 'rgba(16,185,129,0.15)', text: '#34d399' },
  foia: { bg: 'rgba(236,72,153,0.15)', text: '#f472b6' },
  unknown: { bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' },
};

function KindBadge({ kind }) {
  const style = KIND_COLORS[kind] || KIND_COLORS.unknown;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase"
      style={{ background: style.bg, color: style.text }}
    >
      {kind || 'unknown'}
    </span>
  );
}

function ProvenanceList() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState(null);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, listData] = await Promise.all([
        getProvenanceStats(),
        listProvenanceDecisions({ type: typeFilter || undefined, limit, offset: page * limit }),
      ]);
      setStats(statsData);
      setItems(listData.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load provenance');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Classification Provenance</h1>
          <p className="text-gray-600 mt-1">
            Every classification, tearline, and FOIA decision, with the rules the system considered vs used.
          </p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ['Decisions', stats.nodes_by_type?.Decision || 0],
            ['Rules', stats.nodes_by_type?.Rule || 0],
            ['Model calls', stats.nodes_by_type?.ModelCall || 0],
            ['Overrides', stats.edges_by_label?.OVERRIDDEN_BY || 0],
            ['Retrieved→Cited', `${stats.edges_by_label?.RETURNED || 0} → ${stats.edges_by_label?.CITED || 0}`],
          ].map(([label, value]) => (
            <div key={label} className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">{label}</div>
              <div className="text-xl font-semibold mt-1">{value.toLocaleString ? value.toLocaleString() : value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-700">Type:</label>
        <select
          className="border border-gray-300 rounded px-2 py-1 text-sm"
          value={typeFilter}
          onChange={(e) => { setPage(0); setTypeFilter(e.target.value); }}
        >
          <option value="">All</option>
          <option value="classification">Classification</option>
          <option value="tearline">Tearline</option>
          <option value="foia">FOIA</option>
        </select>
        <button
          onClick={load}
          className="text-sm px-3 py-1 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left">
              <th className="px-4 py-2 font-medium text-gray-600">Type</th>
              <th className="px-4 py-2 font-medium text-gray-600">Outcome</th>
              <th className="px-4 py-2 font-medium text-gray-600">Retrieved → Cited</th>
              <th className="px-4 py-2 font-medium text-gray-600">User</th>
              <th className="px-4 py-2 font-medium text-gray-600">When</th>
              <th className="px-4 py-2 font-medium text-gray-600"></th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10">
                  <div className="flex flex-col items-center text-center max-w-md mx-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25" />
                    </svg>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      {typeFilter ? `No ${typeFilter} decisions yet` : 'No classification decisions yet'}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Run a classification, tearline, or FOIA review to populate the provenance graph.
                      Every decision will be traceable here.
                    </p>
                    <div className="flex gap-2">
                      <Link
                        to="/classify"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                      >
                        Run a Classification →
                      </Link>
                      <Link
                        to="/tearline"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                      >
                        Try Tearline
                      </Link>
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {items.map((it) => {
              const delta = (it.retrieved_count || 0) - (it.cited_count || 0);
              return (
                <tr key={it.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/provenance/${encodeURIComponent(it.id)}`)}>
                  <td className="px-4 py-2"><KindBadge kind={it.decision_type} /></td>
                  <td className="px-4 py-2 font-mono text-xs">{it.outcome || '—'}</td>
                  <td className="px-4 py-2">
                    <span className="text-gray-900 font-medium">{it.retrieved_count}</span>
                    <span className="text-gray-400 mx-1">→</span>
                    <span className="text-gray-900 font-medium">{it.cited_count}</span>
                    {delta > 0 && (
                      <span className="ml-2 text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                        {delta} ignored
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600">{it.user_id || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {it.created_at ? new Date(it.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/provenance/${encodeURIComponent(it.id)}`}
                      className="text-blue-600 text-xs hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center">
        <div className="text-xs text-gray-500">Page {page + 1}</div>
        <div className="flex gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40"
          >
            Prev
          </button>
          <button
            disabled={items.length < limit}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function ProvenanceDetail() {
  const { decisionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cascade, setCascade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCascade(null);
    Promise.all([
      getProvenanceDecision(decisionId),
      getCascadeForDecision(decisionId).catch(() => null),
    ])
      .then(([d, c]) => { if (!cancelled) { setData(d); setCascade(c); } })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [decisionId]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const artifact = await exportComplianceArtifact(decisionId);
      const blob = new Blob([JSON.stringify(artifact, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `provenance-${decisionId.replace(/[^a-zA-Z0-9-]/g, '_')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="text-gray-500">Loading decision…</div>;
  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
      {error}
    </div>
  );
  if (!data) return null;

  const citedNodeIds = new Set(data.cited_rules.map((r) => r.node_id));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <button onClick={() => navigate('/provenance')} className="text-sm text-blue-600 hover:underline mb-2">
            ← Back to all decisions
          </button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <KindBadge kind={data.decision_type} />
            Decision Provenance
          </h1>
          <div className="text-xs font-mono text-gray-500 mt-1">{data.id}</div>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {exporting ? 'Exporting…' : 'Export compliance artifact'}
        </button>
      </div>

      {data.props && data.props.self_test_diverged && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg shadow p-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase bg-red-600 text-white">
              ⚠ Self-test diverged
            </span>
            {data.props.self_test_recommendation && (
              <span className="text-xs font-mono text-red-700">
                recommendation: {data.props.self_test_recommendation}
              </span>
            )}
          </div>
          <div className="mt-2 text-sm text-red-900">
            Primary classification:{' '}
            <span className="font-mono font-semibold">{data.props.primary_level || '—'}</span>
            {data.props.self_test_level && (
              <>
                <span className="mx-2 text-red-400">vs. self-test suggests</span>
                <span className="font-mono font-semibold">{data.props.self_test_level}</span>
              </>
            )}
          </div>
          {(data.props.self_test_over_classification_risk || data.props.self_test_under_classification_risk) && (
            <div className="mt-1 text-xs text-red-700">
              {data.props.self_test_over_classification_risk && (
                <span className="mr-3">Over-classification risk: {data.props.self_test_over_classification_risk}</span>
              )}
              {data.props.self_test_under_classification_risk && (
                <span>Under-classification risk: {data.props.self_test_under_classification_risk}</span>
              )}
            </div>
          )}
        </div>
      )}

      {data.artifact && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase mb-1">Outcome</div>
          <div className="text-lg font-mono font-semibold">
            {data.artifact.classification || data.artifact.kind}
          </div>
          {data.artifact.confidence != null && (
            <div className="text-xs text-gray-500 mt-1">
              Confidence: {(data.artifact.confidence * 100).toFixed(1)}%
            </div>
          )}
          {data.artifact.success != null && (
            <div className="text-xs mt-1">
              Success: <span className={data.artifact.success ? 'text-green-700' : 'text-amber-700'}>
                {data.artifact.success ? 'yes' : 'no'}
              </span>
            </div>
          )}
        </div>
      )}

      {data.model_call && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase mb-1">Model call</div>
          <div className="text-sm font-mono">
            {data.model_call.model}
            {data.model_call.service && <span className="text-gray-500"> · {data.model_call.service}</span>}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Tokens: {data.model_call.prompt_tokens || 0} in / {data.model_call.completion_tokens || 0} out
            {data.model_call.estimated_cost != null && (
              <span> · ${Number(data.model_call.estimated_cost).toFixed(6)}</span>
            )}
          </div>
        </div>
      )}

      {/* The headline: retrieved vs cited */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
          <div className="text-sm font-semibold text-gray-700">
            Rules retrieved: <span className="font-mono">{data.retrieved_rules.length}</span>
          </div>
          <div className="text-gray-400">/</div>
          <div className="text-sm font-semibold text-gray-700">
            Cited: <span className="font-mono">{data.cited_rules.length}</span>
          </div>
          {data.delta.length > 0 && (
            <div className="ml-auto text-sm">
              <span className="text-amber-700 font-semibold">{data.delta.length}</span>
              <span className="text-gray-600"> retrieved but not cited (LLM override)</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
          <div>
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase">
              Retrieved (what the system considered)
            </div>
            <div className="max-h-96 overflow-y-auto">
              {data.retrieved_rules.length === 0 && (
                <div className="px-4 py-3 text-sm text-gray-500">No retrieval recorded.</div>
              )}
              {data.retrieved_rules.map((r) => {
                const cited = citedNodeIds.has(r.node_id);
                return (
                  <div
                    key={r.node_id}
                    className={`px-4 py-2 border-b border-gray-100 text-sm ${
                      cited ? 'bg-green-50' : 'bg-amber-50 hover:bg-amber-100'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-500">#{r.rank}</span>
                        <SCGRuleLink ruleId={r.rule_id} />
                        <ViewInPdfButton ruleId={r.rule_id} variant="icon" size="sm" />
                        {cited && <span className="text-xs text-green-700">✓ cited</span>}
                      </div>
                      {r.score != null && (
                        <span className="text-xs text-gray-500 font-mono">{Number(r.score).toFixed(3)}</span>
                      )}
                    </div>
                    {r.classification_level && (
                      <div className="text-xs text-gray-500 mt-0.5">{r.classification_level}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase">
              Cited (what the LLM used)
            </div>
            <div className="max-h-96 overflow-y-auto">
              {data.cited_rules.length === 0 && (
                <div className="px-4 py-3 text-sm text-gray-500">No rules cited by the LLM.</div>
              )}
              {data.cited_rules.map((r) => (
                <div key={r.node_id} className="px-4 py-2 border-b border-gray-100 text-sm bg-green-50">
                  <div className="flex items-center gap-2">
                    <SCGRuleLink ruleId={r.rule_id} />
                    <ViewInPdfButton ruleId={r.rule_id} variant="icon" size="sm" />
                  </div>
                  {r.classification_level && (
                    <div className="text-xs text-gray-500 mt-0.5">{r.classification_level}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {data.overrides && data.overrides.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase mb-2">Analyst overrides</div>
          {data.overrides.map((o, i) => (
            <div key={i} className="border-l-4 border-amber-400 pl-3 py-1 text-sm">
              <div className="font-mono text-xs text-gray-600">{o.analyst}</div>
              <div>Corrected to: <span className="font-mono">{JSON.stringify(o.new_level)}</span></div>
              {o.reason && <div className="text-xs text-gray-600 italic mt-1">"{o.reason}"</div>}
            </div>
          ))}
        </div>
      )}

      {data.injected_feedback_rules && data.injected_feedback_rules.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase mb-2">Injected curated feedback rules</div>
          {data.injected_feedback_rules.map((r) => (
            <div key={r.id} className="text-sm border-l-4 border-indigo-400 pl-3 py-1">
              <div className="font-mono text-xs text-gray-500">{r.id}</div>
              {r.rule_text && <div className="text-sm">{r.rule_text}</div>}
            </div>
          ))}
        </div>
      )}

      {data.document && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase mb-1">Document</div>
          <div className="font-mono text-xs text-gray-500">{data.document.id}</div>
          {data.document.preview && (
            <div className="text-sm text-gray-700 mt-2 italic border-l-2 border-gray-300 pl-3">
              {data.document.preview}
            </div>
          )}
        </div>
      )}

      {cascade && cascade.decisions && cascade.decisions.length > 1 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="text-xs text-gray-500 uppercase mb-2">
            Downstream impact ({cascade.decisions.length - 1} other decision{cascade.decisions.length !== 2 ? 's' : ''} share this document)
          </div>
          <div className="space-y-1">
            {cascade.decisions.filter((d) => d.id !== decisionId).map((d) => (
              <Link
                key={d.id}
                to={`/provenance/${encodeURIComponent(d.id)}`}
                className="flex items-center gap-3 text-sm p-2 hover:bg-gray-50 rounded border-l-4 border-indigo-200"
              >
                <KindBadge kind={d.decision_type} />
                <span className="font-mono text-xs text-gray-600">{d.id}</span>
                <span className="text-xs text-gray-400 ml-auto">
                  {d.created_at ? new Date(d.created_at).toLocaleString() : ''}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProvenanceViewer() {
  const { decisionId } = useParams();
  return decisionId ? <ProvenanceDetail /> : <ProvenanceList />;
}
